/**
 * <s-announcement> — rotating announcement bar.
 * Cycles its [data-slide] children every `interval` ms (default 5000).
 * Pauses while hovered or focused, and a [data-pause] button lets shoppers
 * stop/start rotation (WCAG 2.2.2). Without JS only the first slide shows.
 */
class SAnnouncement extends HTMLElement {
  #index = 0;
  #timer = null;
  #userPaused = false;
  #hoverPaused = false;
  #ac;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.button = this.querySelector('[data-pause]');
    if (this.slides.length < 2) {
      if (this.button) this.button.hidden = true;
      return;
    }
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('pointerenter', () => { this.#hoverPaused = true; this.#sync(); }, opts);
    this.addEventListener('pointerleave', () => { this.#hoverPaused = false; this.#sync(); }, opts);
    this.addEventListener('focusin', () => { this.#hoverPaused = true; this.#sync(); }, opts);
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) { this.#hoverPaused = false; this.#sync(); }
    }, opts);
    this.button?.addEventListener('click', () => {
      this.#userPaused = !this.#userPaused;
      this.button.setAttribute('aria-pressed', String(this.#userPaused));
      this.button.setAttribute('aria-label', this.#userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.#sync();
    }, opts);
    if (this.button) this.button.hidden = false;
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  #sync() {
    clearInterval(this.#timer);
    this.#timer = null;
    if (this.#userPaused || this.#hoverPaused) return;
    const interval = Number(this.getAttribute('interval')) || 5000;
    this.#timer = setInterval(() => this.#show(this.#index + 1), interval);
  }

  #show(next) {
    this.slides[this.#index].hidden = true;
    this.#index = next % this.slides.length;
    this.slides[this.#index].hidden = false;
  }
}

if (!customElements.get('s-announcement')) customElements.define('s-announcement', SAnnouncement);
